import { create } from "zustand";

import type { JobRun } from "@/hooks/useActivity";

/** A job the browser ran this session that ended. Server-recorded work
 * (monitor runs, Field Debate, background scoring, abstract fill-ins) comes
 * from GET /activity instead, so it survives a reload. */
export interface SessionActivity {
  id: string;
  conceptId: string;
  /** OperationBar op type, e.g. "trawl", "pdf". */
  type: string;
  label: string;
  outcome: "success" | "failed" | "cancelled";
  summary: string | null;
  finishedAt: number;
}

const SEEN_KEY = "activity-seen-at";

function readSeen(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) ?? "{}") as Record<string, number>;
  } catch {
    return {};
  }
}

function writeSeen(seen: Record<string, number>) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    // Private window / blocked storage: the count just resets on reload.
  }
}

interface State {
  open: boolean;
  /** Row to scroll to and highlight (an op type), set by a pill click. */
  focus: string | null;
  session: SessionActivity[];
  /** conceptId → when the panel was last opened (ms). Unread = newer. */
  seenAt: Record<string, number>;
  openPanel: (conceptId: string | undefined, focus?: string | null) => void;
  /** Pass the concept so what finished while the panel was open isn't unread. */
  closePanel: (conceptId?: string) => void;
  record: (entry: Omit<SessionActivity, "id">) => void;
  /** Page-run jobs (trawl, snowball) register how to resume a stopped run;
   * their hooks live on Curate. */
  resumers: Record<string, (run: JobRun) => void>;
  registerResumer: (kind: string, resume: ((run: JobRun) => void) | null) => void;
}

export const useActivityStore = create<State>((set) => ({
  open: false,
  focus: null,
  session: [],
  seenAt: readSeen(),
  openPanel: (conceptId, focus = null) =>
    set((s) => {
      if (!conceptId) return { open: true, focus };
      const seenAt = { ...s.seenAt, [conceptId]: Date.now() };
      writeSeen(seenAt);
      return { open: true, focus, seenAt };
    }),
  closePanel: (conceptId) =>
    set((s) => {
      if (!conceptId) return { open: false, focus: null };
      const seenAt = { ...s.seenAt, [conceptId]: Date.now() };
      writeSeen(seenAt);
      return { open: false, focus: null, seenAt };
    }),
  resumers: {},
  registerResumer: (kind, resume) =>
    set((s) => {
      const resumers = { ...s.resumers };
      if (resume) resumers[kind] = resume;
      else delete resumers[kind];
      return { resumers };
    }),
  record: (entry) =>
    set((s) => ({
      session: [{ ...entry, id: `${entry.type}-${entry.finishedAt}` }, ...s.session].slice(0, 50),
    })),
}));
